class LabelSanitizer {
    // Constants
    static CURRENCIES_AS_TEXT = ['EUR', 'CHF', 'GBP', 'USD', 'DKK', 'NOK', 'SEK', 'KZT', 'EUROS', 'EURO', 'US'];

    static MULTI_LETTER_SCALES_DERIVATIVES = [
        'THOUSAND',
        'THOUSANDS',
        'MILLIONS',
        'MILLION',
        'MIL',
        'MILS',
        'BILLION',
        'BILLIONS',
        'BIL',
        'CENTS',
        'MIO',
        'PENCE',
        'BN',
    ];

    static SINGLE_LETTER_SCALES_DERIVATIVES = ['M', 'B', 'K', 'T'];

    static ALL_SCALES =
        LabelSanitizer.MULTI_LETTER_SCALES_DERIVATIVES.join('|') +
        '|' +
        LabelSanitizer.SINGLE_LETTER_SCALES_DERIVATIVES.join('|');

    static ALL_CURRENCIES = LabelSanitizer.CURRENCIES_AS_TEXT.join('|');

    // Pattern strings (reusable)
    static PATTERN_SCALE_AND_CURRENCY_COMBINATIONS =
        '(?:in\\s+)?' +
        '(?:' +
        '(?<![a-zA-Z&\\-/])(?:' +
        LabelSanitizer.ALL_SCALES +
        ')\\.*\\s*(?:of\\s+)?(?:' +
        LabelSanitizer.ALL_CURRENCIES +
        '|\\p{Sc}' +
        ')(?![a-zA-Z&\\-/])|' +
        '(?:' +
        LabelSanitizer.ALL_CURRENCIES +
        '|\\p{Sc}' +
        ')\\s*(?:' +
        LabelSanitizer.ALL_SCALES +
        ')\\.*(?![a-zA-Z&\\-/])|' +
        '(?<![a-zA-Z&\\-/])(?:' +
        LabelSanitizer.ALL_CURRENCIES +
        ')(?![a-zA-Z&\\-/])|\\p{Sc}|' +
        '(?<![a-zA-Z&\\-/])(?:' +
        LabelSanitizer.ALL_SCALES +
        ')\\.*(?![a-zA-Z&\\-/])' +
        ')';

    static PATTERN_CLEAN_BEGINNING_PART =
        '\\s*(?:' +
        '[a-zA-Z]\\.?\\)?[0-9]{0,2}\\.?\\)?|' +
        '[0-9]+\\.?\\)?[a-zA-Z]\\.?\\)?|' +
        '\\(?\\s*[0-9]+\\.?\\)?|' +
        '[a-zA-Z]\\.?\\)?' +
        ')\\s+';

    static PATTERN_STANDALONE_SYMBOL_IN_PARENTHESES = '\\s*\\([^%]\\)\\s*';

    static PATTERN_SUPERSCRIPT_CHARS = '[(⁽]*\\s*[⁰¹²³⁴⁵⁶⁷⁸⁹\\p{Sk}\\p{Lm}]+\\s*[)⁾]*';

    static PATTERN_NOTE = '\\b(?:see\\s+)?(Note|Notes)\\s*(?![a-zA-Z])[0-9.]*';

    static PATTERN_PER_SHARE = '\\(\\s*(?:cents|pence|p)?\\s*per\\s*share\\s*\\)';

    static PATTERN_CLEAN_END_PART =
        '\\s+(?:' +
        '\\(?[a-zA-Z]\\.?\\)?\\(?[0-9]+\\.?\\)?|' +
        '\\(?[0-9]+\\.?\\)?\\(?[a-zA-Z]\\.?\\)?|' +
        '\\(?[0-9]+\\.?\\)?|' +
        '\\(?[a-zA-Z]\\.?\\)?' +
        ')\\s*';

    // Compiled regexes
    static REGEX_SCALE_AND_CURRENCY_COMBINATIONS = new RegExp(
        '(' + LabelSanitizer.PATTERN_SCALE_AND_CURRENCY_COMBINATIONS + ')',
        'giu'
    );

    static REGEX_CLEAN_BEGINNING = new RegExp(
        '^(' + '\\bless\\s*:|' + LabelSanitizer.PATTERN_CLEAN_BEGINNING_PART + '|' + '[^\\p{L}()%]' + ')+',
        'giu'
    );

    static NEW_REGEX_SUPERSCRIPT_CHARS = new RegExp(LabelSanitizer.PATTERN_SUPERSCRIPT_CHARS, 'gu');

    static NEW_REGEX_STANDALONE_SYMBOL_IN_PARENTHESES = new RegExp(
        LabelSanitizer.PATTERN_STANDALONE_SYMBOL_IN_PARENTHESES,
        'gm'
    );

    static REGEX_NOTE = new RegExp(LabelSanitizer.PATTERN_NOTE, 'gi');

    static REGEX_PER_SHARE = new RegExp(LabelSanitizer.PATTERN_PER_SHARE, 'gi');

    static REGEX_CLEAN_EVERYWHERE = new RegExp(
        '(' +
            LabelSanitizer.PATTERN_STANDALONE_SYMBOL_IN_PARENTHESES +
            '|' +
            LabelSanitizer.PATTERN_SCALE_AND_CURRENCY_COMBINATIONS +
            '|' +
            LabelSanitizer.PATTERN_PER_SHARE +
            '|' +
            LabelSanitizer.PATTERN_SUPERSCRIPT_CHARS +
            '|' +
            '\\([a-zA-Z]\\)(?:\\s*[+=-]\\s*\\([a-zA-Z]\\))+' +
            '|' +
            '\\([a-zA-Z]\\s*[+=-]\\s*[a-zA-Z]\\)+' +
            ')+',
        'giu'
    );

    static REGEX_CLEAN_END = new RegExp(
        '(' +
            LabelSanitizer.PATTERN_SCALE_AND_CURRENCY_COMBINATIONS +
            '|' +
            LabelSanitizer.PATTERN_PER_SHARE +
            '|' +
            LabelSanitizer.PATTERN_CLEAN_END_PART +
            '|' +
            LabelSanitizer.PATTERN_NOTE +
            '|' +
            '[^\\p{L}()%.]+)+$',
        'giu'
    );

    // Note: The order of the regexes is important.
    static REGEXES_TO_MATCH = [
        LabelSanitizer.REGEX_CLEAN_EVERYWHERE,
        LabelSanitizer.REGEX_CLEAN_BEGINNING,
        LabelSanitizer.REGEX_CLEAN_END,
        new RegExp('\\s{2,}', 'g'),
    ];

    static PROTECTED_PATTERNS = new RegExp('(?:(IAS|IFRS)\\s*[0-9]+)|(?:class[\\s-]+[A-D])', 'iu');

    static sanitizeLabel(label: string): string {
        if (label === null || label === undefined) {
            throw new Error('Label is required for sanitization');
        }

        const protectedZ: string[] = [];
        const arr = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j'];
        let counter = 0;

        // Find and replace protected patterns
        label = label.replace(LabelSanitizer.PROTECTED_PATTERNS, (match) => {
            protectedZ.push(match);
            return 'PROTECTED' + arr[counter++];
        });

        let result = label;

        // Apply regex replacements in order
        for (const regex of LabelSanitizer.REGEXES_TO_MATCH) {
            result = result.replace(regex, ' ');
        }

        // Remove unmatched brackets
        result = LabelSanitizer.removeUnmatchedBrackets(result);

        // Restore protected patterns
        for (let i = 0; i < protectedZ.length; i++) {
            result = result.replace('PROTECTED' + arr[i], protectedZ[i]);
        }

        // Apply additional string replacements
        result = result
            .replace(/\(\/[A-Za-z]\)/g, '')
            .replace(/\(\s*,\s*/g, '(')
            .replace(/\s*,\s*\)/g, ')')
            .replace(/\*/g, '')
            .replace(/\s*\/\s*/g, '/')
            .replace(/\([\s*.]*\)/g, '')
            .replace(/\(\s*\/\s*Share\s*\)/gi, "")
            .trim();

        return result;
    }

    static removeUnmatchedBrackets(text: string): string {
        const unmatchedIndices = LabelSanitizer.findUnmatchedBrackets(text);
        let result = '';

        for (let i = 0; i < text.length; i++) {
            if (!unmatchedIndices.has(i)) {
                result += text.charAt(i);
            }
        }

        return result;
    }

    static findUnmatchedBrackets(text: string): Set<number> {
        const unmatched = new Set<number>();
        const stack: number[] = [];

        for (let i = 0; i < text.length; i++) {
            const c = text.charAt(i);

            if (c === '(') {
                stack.push(i);
            } else if (c === ')') {
                if (stack.length === 0) {
                    unmatched.add(i);
                } else {
                    stack.pop();
                }
            }
        }

        // Add remaining unmatched opening brackets
        for (const index of stack) {
            unmatched.add(index);
        }

        return unmatched;
    }
}

export { LabelSanitizer };
